import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const SafeAnimatePresence = AnimatePresence as any;

interface AccordionItemProps {
  title: string | JSX.Element;
  isOpen: boolean;
  onClick: () => void;
  children: React.ReactNode;
}

const AccordionItem: React.FC<AccordionItemProps> = ({ title, isOpen, onClick, children }) => {
  return (
    <div className={`mb-3 overflow-hidden rounded-[2rem] border transition-all duration-300 ${
      isOpen 
        ? "bg-white border-primary/20 shadow-xl shadow-primary/5" 
        : "bg-slate-50/50 border-slate-100 hover:border-slate-200"
    }`}>
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-6 text-left"
      >
        <span className={`text-sm font-black uppercase tracking-widest transition-colors ${
          isOpen ? "text-primary" : "text-slate-500"
        }`}>
          {title}
        </span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3, ease: "circOut" }}
          className={isOpen ? "text-primary" : "text-slate-400"}
        >
          <ChevronDown size={20} />
        </motion.div>
      </button>

      <SafeAnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "circOut" }}
          >
            <div className="px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 md:pb-8 pt-2 border-t border-slate-100/50">
               <div className="prose prose-slate max-w-none overflow-x-auto">
                 {children}
               </div>
            </div>
          </motion.div>
        )}
      </SafeAnimatePresence>
    </div>
  );
};

interface AccordionProps {
  items: {
    title: string | JSX.Element;
    content: string | JSX.Element;
  }[];
}

const Accordion: React.FC<AccordionProps> = ({ items }) => {
  const [openIndices, setOpenIndices] = useState<number[]>([]);
  const itemRefs = useRef<(HTMLDivElement | null)[]>([]); 

  const handleClick = (index: number) => {
    setOpenIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  useEffect(() => {
    if (openIndices.length > 0) {
      const last = openIndices[openIndices.length - 1];
      itemRefs.current[last]?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [openIndices]);

  return (
    <div className="space-y-1">
      {items.map((item, index) => (
        <div key={index} ref={el => (itemRefs.current[index] = el)}>
          <AccordionItem
            title={item.title}
            isOpen={openIndices.includes(index)}
            onClick={() => handleClick(index)}
          >
            {item.content}
          </AccordionItem>
        </div>
      ))}
    </div>
  );
};

export default Accordion;
